'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { getAgeStatus, setAgeStatus, AGE_VERIFIED, AGE_DENIED } from '@/lib/ageGate';

const AGE_RESTRICTED_PATH = '/age-restricted';

export default function AgeVerificationGate() {
  // `null` = not yet determined (avoids hydration flash); true = prompt needed.
  const [needsGate, setNeedsGate] = useState<boolean | null>(null);

  useEffect(() => {
    // Only prompt when no decision has been made yet. A `denied` cookie is
    // handled by the server middleware (redirect to /age-restricted), so we
    // never show the modal on top of that screen.
    setNeedsGate(getAgeStatus() === null);
  }, []);

  // Lock body scroll while the gate is visible.
  useEffect(() => {
    if (needsGate === true) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [needsGate]);

  const handleConfirm = () => {
    setAgeStatus(AGE_VERIFIED);
    setNeedsGate(false);
  };

  const handleDecline = () => {
    setAgeStatus(AGE_DENIED);
    // Force a full navigation so the server middleware enforces the block.
    window.location.href = AGE_RESTRICTED_PATH;
  };

  // Don't render until we know, or when no prompt is needed.
  if (needsGate !== true) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-8 sm:p-10 text-center shadow-2xl">
        {/* 18+ badge */}
        <div className="relative mx-auto mb-8 h-24 w-24">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-purple-500/15 ring-1 ring-inset ring-purple-500/30">
            <span className="text-3xl font-bold text-white">18</span>
            {/* diagonal strike */}
            <span className="pointer-events-none absolute left-1/2 top-1/2 h-[3px] w-28 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-purple-400" />
          </div>
          {/* warning badge */}
          <span className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#0a0a0a] bg-red-500 text-lg font-bold text-white">
            !
          </span>
        </div>

        <h1
          id="age-gate-title"
          className="mb-5 text-3xl font-bold tracking-tight text-white sm:text-4xl"
        >
          Age Verification Required
        </h1>

        <p className="mb-8 text-base leading-relaxed text-white/60">
          Ultragoon contains AI-generated content intended for adults only. By entering, you
          confirm that you are at least 18 years old and that adult content is legal in your
          jurisdiction.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={handleDecline}
            className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.02] px-6 py-3 text-sm font-medium text-white/70 transition-all hover:bg-white/[0.04] hover:text-white active:scale-95"
          >
            I&apos;m under 18
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="flex-1 rounded-lg bg-white px-6 py-3 text-sm font-medium text-black shadow-lg shadow-black/20 transition-all hover:bg-white/90 active:scale-95"
          >
            I&apos;m 18 or older
          </button>
        </div>

        <p className="mt-7 text-sm leading-relaxed text-white/40">
          By continuing, you agree to our{' '}
          <Link href="/terms" className="text-purple-400 underline hover:text-purple-300">
            Terms of Service
          </Link>{' '}
          and acknowledge our{' '}
          <Link href="/fair-use" className="text-purple-400 underline hover:text-purple-300">
            Fair Use Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
